import { Campaign, CampaignType } from '../types/campaign';
import { Collab, CollabStatus, ContentDeadlineKind } from '../types/collab';
import { resolveSchedulingLeadDays } from './schedulingLeadTimeUtils';

export const MIN_CONTENT_TURNAROUND_DAYS = 1;
export const MAX_CONTENT_TURNAROUND_DAYS = 30;
export const MAX_DUE_DATE_EXTENSION_DAYS = 30;

// Collabs that agreed terms and still owe content — the only statuses whose due date a brand can push. collabRedeemed is a legacy in-store status (redeemed, content pending) no current code writes.
export const DUE_DATE_EXTENDABLE_STATUSES: readonly CollabStatus[] = ['pendingRedemption', 'collabRedeemed', 'pendingShipment', 'pendingSubmission', 'pendingResubmission'];

/** Days to submit content when the campaign sets none: in-store 7, every other type 14. */
export const DEFAULT_CONTENT_TURNAROUND_DAYS: Record<CampaignType, number> = {
  inStore: 7,
  shipToHome: 14,
  shopify: 14,
  retail: 14,
  brandManaged: 14,
};

export const resolveTurnaroundDays = (
  campaign: Pick<Campaign, 'campaignType' | 'contentTurnaroundDays'>
): number =>
  campaign.contentTurnaroundDays ??
  (campaign.campaignType ? DEFAULT_CONTENT_TURNAROUND_DAYS[campaign.campaignType] : undefined) ??
  14;

/** Offset (ms) of `timezone` from UTC at the given instant. */
const tzOffsetMs = (instant: number, timezone: string): number => {
  const p = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    hourCycle: 'h23',
    year: 'numeric', month: 'numeric', day: 'numeric',
    hour: 'numeric', minute: 'numeric', second: 'numeric',
  }).formatToParts(new Date(instant)).reduce<Record<string, number>>((a, x) => {
    if (x.type !== 'literal') a[x.type] = Number(x.value);
    return a;
  }, {});
  return Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second) - Math.floor(instant / 1000) * 1000;
};

/** Wall-clock time (expressed as if UTC) in `timezone` -> real instant. Two passes absorb a DST change between guess and answer. */
const wallToInstant = (wall: number, timezone: string): Date => {
  let instant = wall - tzOffsetMs(wall, timezone);
  instant = wall - tzOffsetMs(instant, timezone);
  return new Date(instant);
};

/**
 * 23:59:59.999 on the calendar day that is (start's local day in `timezone`) + turnaround +
 * extension days, as a UTC instant. `timezone` is an IANA name.
 */
export const computeContentDueDate = (
  start: Date,
  turnaroundDays: number,
  extensionDays: number,
  timezone: string
): Date => {
  const local = new Date(start.getTime() + tzOffsetMs(start.getTime(), timezone));
  // Wall-clock end of the due day, expressed as if it were UTC.
  const wall = Date.UTC(
    local.getUTCFullYear(),
    local.getUTCMonth(),
    local.getUTCDate() + turnaroundDays + (extensionDays || 0),
    23, 59, 59, 999
  );
  return wallToInstant(wall, timezone);
};

/** The go-live window as stored on a campaign or a collab. */
export type GoLiveFields = Pick<Campaign, 'contentGoLiveStart' | 'contentGoLiveEnd'>;

/** A campaign or collab with a valid go-live window (both days real, start <= end). */
export const isPostByCampaign = (target: GoLiveFields | null | undefined): boolean =>
  isValidYmd(target?.contentGoLiveStart) &&
  isValidYmd(target?.contentGoLiveEnd) &&
  target!.contentGoLiveStart! <= target!.contentGoLiveEnd!;

const YMD_RE = /^\d{4}-\d{2}-\d{2}$/;

/** Strict 'YYYY-MM-DD' that is a real calendar date. */
export const isValidYmd = (v: unknown): v is string => {
  if (typeof v !== 'string' || !YMD_RE.test(v)) return false;
  const d = new Date(ymdToUtcMs(v));
  return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === v;
};

/** `timezone` if it's a valid IANA name, else 'UTC'. */
export const safeTz = (timezone?: string | null): string => {
  if (!timezone) return 'UTC';
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: timezone });
    return timezone;
  } catch {
    return 'UTC';
  }
};

const ymdToUtcMs = (ymd: string): number => {
  const [y, m, d] = ymd.split('-').map(Number);
  return Date.UTC(y, m - 1, d);
};

/** The calendar day of `instant` in `timezone`, as UTC midnight ms. */
const localDayMs = (instant: Date, timezone: string): number => {
  const local = new Date(instant.getTime() + tzOffsetMs(instant.getTime(), timezone));
  return Date.UTC(local.getUTCFullYear(), local.getUTCMonth(), local.getUTCDate());
};

const MS_PER_DAY = 86400000;

/** 23:59:59.999 of calendar day `ymd` in `timezone` (UTC when missing/invalid). */
export const endOfDayInTimezone = (ymd: string, timezone?: string | null): Date =>
  wallToInstant(ymdToUtcMs(ymd) + MS_PER_DAY - 1, safeTz(timezone));

/** Whole calendar days from the acceptance day (in tz) to the post-by day, minimum 1. */
export const derivedTurnaroundDays = (acceptedAt: Date, postByYmd: string, timezone?: string | null): number =>
  Math.max(1, Math.round((ymdToUtcMs(postByYmd) - localDayMs(acceptedAt, safeTz(timezone))) / MS_PER_DAY));

/** Calendar days from today (in tz) to the post-by day: 0 = today, negative = passed. */
export const daysUntilPostBy = (postByYmd: string, now: Date, timezone?: string | null): number =>
  Math.round((ymdToUtcMs(postByYmd) - localDayMs(now, safeTz(timezone))) / MS_PER_DAY);

/** The go-live window has ended (past the end of its last day in tz). False when there is no go-live. */
export const isPostByPassed = (target: GoLiveFields | null | undefined, now: Date, timezone?: string | null): boolean =>
  isPostByCampaign(target) && now.getTime() > endOfDayInTimezone(target!.contentGoLiveEnd!, timezone).getTime();

/** From the string only (no timezone shift): short "Nov 15", long "Sat, Nov 15". */
export const formatPostByDate = (ymd: string, style: 'short' | 'long'): string =>
  new Date(ymdToUtcMs(ymd)).toLocaleDateString('en-US', {
    timeZone: 'UTC',
    month: 'short',
    day: 'numeric',
    ...(style === 'long' ? { weekday: 'short' } : {}),
  });

/** Ship-to-home shipping grace: under SHIPPING_GRACE_UNDER_TURNAROUND days, the creator's days start
 * when they receive the product, or SHIPPING_GRACE_DAYS after shipping if they never confirm. */
export const SHIPPING_GRACE_DAYS = 4;
export const SHIPPING_GRACE_UNDER_TURNAROUND = 7;

/** Pass the campaign's type, or a custom collab's customCollabType. Go-live collabs are the caller's to exclude. */
export const shippingGraceApplies = (
  campaignType: CampaignType | null | undefined,
  turnaroundDays: number | null | undefined
): boolean =>
  campaignType === 'shipToHome' && turnaroundDays != null && turnaroundDays < SHIPPING_GRACE_UNDER_TURNAROUND;

/**
 * When a shipped collab's content clock starts. With the grace: receipt, or shipped + 4 days if that
 * is earlier. Without: the earlier of shipping and receipt. Null when neither has happened.
 * ponytail: +4 x 24h, not 4 calendar days; within an hour of midnight on a DST-change night the
 * start lands a day off. Pass a timezone and step calendar days if that ever matters.
 */
export const shippingClockStart = (shippedAt: Date | null, receivedAt: Date | null, applies: boolean): Date | null => {
  const fromShipping = shippedAt && (applies ? new Date(shippedAt.getTime() + SHIPPING_GRACE_DAYS * MS_PER_DAY) : shippedAt);
  if (!fromShipping || !receivedAt) return fromShipping ?? receivedAt;
  return receivedAt.getTime() < fromShipping.getTime() ? receivedAt : fromShipping;
};

/** Days the setup check assumes a ship-to-home product takes to arrive: the shipping grace. */
export const ASSUMED_SHIPPING_DAYS = SHIPPING_GRACE_DAYS;
/** Days held back before go-live start for the brand to review a draft (Flask auto-approves after 5). */
export const DRAFT_REVIEW_DAYS = 5;

const addDaysYmd = (ymd: string, days: number): string => new Date(ymdToUtcMs(ymd) + days * MS_PER_DAY).toISOString().slice(0, 10);
/** Whole calendar days from `a` to `b` (both 'YYYY-MM-DD'). */
const ymdDiffDays = (a: string, b: string): number => Math.round((ymdToUtcMs(b) - ymdToUtcMs(a)) / MS_PER_DAY);

/**
 * Latest a draft can be due on a go-live campaign: end of (go-live start - DRAFT_REVIEW_DAYS - 1)
 * in tz. The extra day: a draft sent at 23:59 on its due day auto-approves DRAFT_REVIEW_DAYS x 24h
 * later, which still lands before go-live start. Flask's cap matches.
 */
export const draftDueCap = (goLiveStartYmd: string, timezone?: string | null): Date =>
  endOfDayInTimezone(addDaysYmd(goLiveStartYmd, -DRAFT_REVIEW_DAYS - 1), timezone);

/**
 * True when a draft due `turnaroundDays` (+ `shippingDays` before the clock starts) after
 * `clockStart` would land after draftDueCap. The one predicate behind both the wizard heads-up
 * (clockStart = campaign start, shippingDays = ASSUMED_SHIPPING_DAYS on ship-to-home) and the real
 * move at clock start (applyDraftDueCap).
 */
export const wouldDraftMissGoLive = (params: {
  clockStart: Date;
  turnaroundDays: number;
  goLiveStart: string;
  timezone?: string | null;
  shippingDays?: number;
}): boolean => {
  const tz = safeTz(params.timezone);
  const due = computeContentDueDate(params.clockStart, params.turnaroundDays + (params.shippingDays ?? 0), 0, tz);
  return due.getTime() > draftDueCap(params.goLiveStart, tz).getTime();
};

/**
 * Draft due date for a collab whose clock starts at `clockStart`:
 * max(clockStart + 1 day, min(clockStart + turnaround, draftDueCap)). Unchanged
 * (clockStart + turnaround + extension) unless the collab is a draft collab with a go-live window.
 */
export const applyDraftDueCap = (
  clockStart: Date,
  turnaroundDays: number,
  extensionDays: number,
  collab: GoLiveFields & { contentDeadlineKind?: ContentDeadlineKind },
  timezone?: string | null
): Date => {
  const tz = safeTz(timezone);
  const due = computeContentDueDate(clockStart, turnaroundDays, extensionDays, tz);
  if (collab.contentDeadlineKind !== 'draft' || !isPostByCampaign(collab)) return due;
  if (!wouldDraftMissGoLive({ clockStart, turnaroundDays: turnaroundDays + (extensionDays || 0), goLiveStart: collab.contentGoLiveStart!, timezone: tz })) return due;
  const floor = computeContentDueDate(clockStart, 1, 0, tz);
  const cap = draftDueCap(collab.contentGoLiveStart!, tz);
  return cap.getTime() < floor.getTime() ? floor : cap;
};

const hasKeys = (o: object | null | undefined): boolean => !!o && Object.keys(o).length > 0;

/**
 * What a collab's contentDueDate means. The kind stamped at acceptance wins; otherwise draft when the
 * campaign requires brand review (draft toggle, UGC, or content requirements on campaign or collab).
 */
export const deadlineKindFor = (
  campaign: Pick<Campaign, 'requireDraftApproval' | 'creatorType' | 'contentRequirements'> | null | undefined,
  collab?: Pick<Collab, 'contentDeadlineKind' | 'contentRequirements' | 'content'> | null
): ContentDeadlineKind => {
  if (collab?.contentDeadlineKind) return collab.contentDeadlineKind;
  const draft =
    !!campaign?.requireDraftApproval ||
    campaign?.creatorType === 'ugc' ||
    hasKeys(campaign?.contentRequirements) ||
    hasKeys(collab?.contentRequirements) ||
    hasKeys(collab?.content);
  return draft ? 'draft' : 'post';
};

/** Neither party has accepted yet: the campaign's current go-live still applies. */
export const PRE_ACCEPT_STATUSES: readonly CollabStatus[] = ['pendingInfluencerApproval', 'pendingClientApproval', 'influencerCountered'];

/** A go-live window as calendar days ('YYYY-MM-DD'), start <= end. */
export type GoLiveWindow = { start: string; end: string };

/** The window stored on a campaign or collab, or null when it has none (or an invalid one). */
export const windowOf = (target: GoLiveFields | null | undefined): GoLiveWindow | null =>
  isPostByCampaign(target) ? { start: target!.contentGoLiveStart!, end: target!.contentGoLiveEnd! } : null;

/** long: "on Sat, Nov 15" / "between Thu, Nov 13 and Sat, Nov 15"; short drops the weekday. */
export const goLiveLabel = (window: GoLiveWindow, style: 'short' | 'long' = 'long'): string =>
  window.start === window.end
    ? `on ${formatPostByDate(window.start, style)}`
    : `between ${formatPostByDate(window.start, style)} and ${formatPostByDate(window.end, style)}`;

/** The collab fields go-live rules read (all optional, so a partial collab or a lock write fits). */
export type GoLiveCollab = GoLiveFields & Partial<Pick<Collab, 'status' | 'contentTurnaroundDays' | 'contentDeadlineKind' | 'contentRequirements' | 'content' | 'deliverableLinks' | 'scheduledCollabDate'>>;
export type GoLiveCampaign = GoLiveFields & Pick<Campaign, 'requireDraftApproval' | 'creatorType' | 'contentRequirements'>;

/**
 * The go-live window a collab is held to, or null (no go-live). A collab whose terms are locked
 * (turnaround stamped) or that is past the pre-accept statuses keeps its own window; otherwise the
 * campaign's CURRENT window applies, since that is what an accept now would stamp.
 */
export const goLiveWindowFor = (
  collab: GoLiveCollab | null | undefined,
  campaign: GoLiveFields | null | undefined
): GoLiveWindow | null => {
  const locked = !!collab && (collab.contentTurnaroundDays != null || (collab.status != null && !PRE_ACCEPT_STATUSES.includes(collab.status)));
  return locked ? windowOf(collab) : windowOf(campaign);
};

/**
 * The stamped kind; a go-live collab without a stamp counts as a draft collab. The kind is always
 * stamped together with the window, so this only covers a malformed doc, and draft is the earlier,
 * safer cap. (Flask differs here: it caps only on an explicit 'draft'.) Raw stamp only: goLiveKind
 * also applies draftLockReleased.
 */
export const stampedKind = (collab: GoLiveCollab): ContentDeadlineKind | undefined =>
  collab.contentDeadlineKind ?? (isPostByCampaign(collab) ? 'draft' : undefined);

/**
 * A 'draft' stamp that no longer binds: the brand turned drafts off after this collab accepted, so
 * the campaign alone would now make it a post collab (UGC or content requirements on the campaign
 * or collab keep it draft). Only while no visit is booked and before redemption (status
 * pendingRedemption, no deliverable links), so it never contradicts a due date and reminders built
 * from the draft lock: booking a released collab restamps it post (scheduleInStoreVisit), and a
 * brand reschedule request clears the visit, so a booked collab is released again when it rebooks.
 * contentDueDate is not a signal: every booking writes one and a reschedule request leaves it. Only
 * ever loosens a draft to post, never the reverse. Without it the visit stays capped at go-live
 * start - 7 for a draft the brand no longer asks for.
 */
export const draftLockReleased = (
  campaign: GoLiveCampaign | null | undefined,
  collab: GoLiveCollab | null | undefined
): boolean =>
  !!collab &&
  collab.contentDeadlineKind === 'draft' &&
  collab.status === 'pendingRedemption' &&
  !hasKeys(collab.deliverableLinks) &&
  !collab.scheduledCollabDate &&
  deadlineKindFor(campaign, { ...collab, contentDeadlineKind: undefined }) === 'post';

/**
 * The kind a go-live collab is held to: the stamp on an accepted collab (post when
 * draftLockReleased), else derived from the campaign.
 */
export const goLiveKind = (
  campaign: GoLiveCampaign | null | undefined,
  collab: GoLiveCollab | null | undefined
): ContentDeadlineKind => {
  const accepted = !!collab?.status && !PRE_ACCEPT_STATUSES.includes(collab.status);
  if (accepted && draftLockReleased(campaign, collab)) return 'post';
  return (accepted ? stampedKind(collab!) : collab?.contentDeadlineKind) ?? deadlineKindFor(campaign, collab);
};

/**
 * Last instant a visit can be on (end of that day in `timezone`), or null without go-live.
 * Post collabs: go-live end. Draft collabs: the day BEFORE the draft cap, because the earliest a
 * draft can be due is visit + 1 day (applyDraftDueCap's floor) and that must not pass draftDueCap.
 * The same boundary as goLiveBlocker's 'unreachable', so accept and booking agree.
 */
export const visitDeadline = (
  collab: GoLiveCollab | null | undefined,
  campaign: GoLiveCampaign | null | undefined,
  timezone: string
): Date | null => {
  const window = goLiveWindowFor(collab, campaign);
  if (!window) return null;
  return goLiveKind(campaign, collab) === 'draft'
    ? draftDueCap(addDaysYmd(window.start, -1), timezone)
    : endOfDayInTimezone(window.end, timezone);
};

export type GoLiveBlocker = 'passed' | 'unreachable';

/**
 * Why a collab whose clock starts at `clockStart` can't make go-live, or null:
 * - 'passed': the window has ended;
 * - 'unreachable': draft collab whose earliest draft due date (clock start + 1 day) is after the
 *   draft cap (so a clock starting after visitDeadline), or post collab whose clock starts after
 *   the window ends.
 */
export const goLiveBlocker = (
  window: GoLiveWindow,
  kind: ContentDeadlineKind,
  clockStart: Date,
  now: Date,
  timezone: string
): GoLiveBlocker | null => {
  const tz = safeTz(timezone);
  const end = endOfDayInTimezone(window.end, tz).getTime();
  if (now.getTime() > end) return 'passed';
  if (kind === 'post') return clockStart.getTime() > end ? 'unreachable' : null;
  return computeContentDueDate(clockStart, 1, 0, tz).getTime() > draftDueCap(window.start, tz).getTime() ? 'unreachable' : null;
};

/** One-click ways out of a go-live campaign creators can't reach. */
export interface VisitReachabilityFixes {
  /** Go-live window shifted later (same length) so the latest date that matters fits the draft buffer. */
  moveGoLiveTo?: { start: string; end: string };
  /** Turning the draft toggle off makes this a post campaign (false for UGC or content requirements, which keep it draft). */
  disableDrafts: boolean;
  /** In-store specific dates: add one on or before this day. */
  addDatesBy?: string;
}

export interface VisitReachability {
  kind: ContentDeadlineKind;
  window: GoLiveWindow;
  /**
   * Last calendar day the content clock can start on and still make go-live: the last visit day for
   * in-store (visitDeadline's day), the last accept/arrival day otherwise. Draft: go-live start - 7.
   * Post: go-live end.
   */
  lastStartYmd: string;
  /** Earliest calendar day the clock can start from today (lead days for open hours, shipping grace). */
  earliestStartYmd: string;
  /** In-store specific dates still ahead of today: on or before lastStartYmd vs after it. */
  bookableDates: string[];
  cutDates: string[];
  /** ok: nothing cut; partial: some specific dates cut; none: creators can't make go-live at all. */
  status: 'ok' | 'partial' | 'none';
  fixes: VisitReachabilityFixes;
}

/**
 * What creators can actually book on a go-live campaign, from the brand's side, before anyone
 * accepts. The campaign-level twin of visitDeadline/goLiveBlocker: the same draft cap, applied to
 * the campaign's own dates so an impossible setup is caught at save, not on the creator's phone.
 * Null without a go-live window, or for specific dates that have all passed (nothing to judge).
 */
export const visitReachability = (
  campaign: GoLiveCampaign & Partial<Pick<Campaign, 'campaignType' | 'availableTimes' | 'minimumSchedulingLeadDays'>>,
  now: Date,
  timezone?: string | null
): VisitReachability | null => {
  const window = windowOf(campaign);
  if (!window) return null;
  const tz = safeTz(timezone);
  const kind = deadlineKindFor(campaign);
  const todayYmd = new Date(localDayMs(now, tz)).toISOString().slice(0, 10);
  const lastStartYmd = kind === 'draft' ? addDaysYmd(window.start, -DRAFT_REVIEW_DAYS - 2) : window.end;
  const specific = campaign.campaignType === 'inStore' ? Object.keys(campaign.availableTimes ?? {}).filter((d) => d >= todayYmd).sort() : [];
  const usesSpecificDates = campaign.campaignType === 'inStore' && hasKeys(campaign.availableTimes);
  if (usesSpecificDates && specific.length === 0) return null;

  // ponytail: open hours checked by day only (lead days), not closed weekdays; the wizard's
  // getLastChanceForCreators has the hour-level picture.
  const leadDays =
    // Unset lead days mean the scheduler's default (1), as accept reads them.
    campaign.campaignType === 'inStore' ? (usesSpecificDates ? 0 : resolveSchedulingLeadDays(campaign))
    // Shipping grace on draft campaigns only, as earliestClockStart does at accept.
    : kind === 'draft' && (campaign.campaignType === 'shipToHome' || campaign.campaignType === 'shopify') ? ASSUMED_SHIPPING_DAYS
    : 0;
  const earliestStartYmd = usesSpecificDates ? specific[0] : addDaysYmd(todayYmd, leadDays);
  const bookableDates = specific.filter((d) => d <= lastStartYmd);
  const cutDates = specific.filter((d) => d > lastStartYmd);
  const reachable = usesSpecificDates ? bookableDates.length > 0 : earliestStartYmd <= lastStartYmd;
  const status = !reachable ? 'none' : cutDates.length > 0 ? 'partial' : 'ok';

  // Shift go-live so the LAST date that matters fits: the latest specific date, else the earliest start.
  const mustFit = usesSpecificDates ? specific[specific.length - 1] : earliestStartYmd;
  const shift = ymdDiffDays(lastStartYmd, mustFit);
  const fixes: VisitReachabilityFixes = {
    moveGoLiveTo: status === 'ok' || shift <= 0 ? undefined : { start: addDaysYmd(window.start, shift), end: addDaysYmd(window.end, shift) },
    disableDrafts: status !== 'ok' && kind === 'draft' && deadlineKindFor({ ...campaign, requireDraftApproval: false }) === 'post',
    addDatesBy: status === 'none' && usesSpecificDates && lastStartYmd >= todayYmd ? lastStartYmd : undefined,
  };
  return { kind, window, lastStartYmd, earliestStartYmd, bookableDates, cutDates, status, fixes };
};

/** One sentence for the brand on why creators can't make go-live (status 'none'), by what blocks it. */
export const reachabilityMessage = (r: VisitReachability, campaignType: CampaignType | null | undefined): string => {
  const last = formatPostByDate(r.lastStartYmd, 'long');
  const goLive = formatPostByDate(r.window.start, 'short');
  const why = r.kind === 'draft'
    ? `Drafts need ${DRAFT_REVIEW_DAYS} days of review before go-live on ${goLive}, so`
    : `Content must be live by ${formatPostByDate(r.window.end, 'short')}, so`;
  if (r.cutDates.length > 0) return `${why} creators must visit by ${last}. None of your visit dates are on or before then.`;
  if (campaignType === 'inStore') return `${why} creators must visit by ${last}, but your booking lead time makes the earliest visit ${formatPostByDate(r.earliestStartYmd, 'long')}.`;
  if (campaignType === 'shipToHome' || campaignType === 'shopify') return `${why} products must arrive by ${last}, but shipping takes about ${ASSUMED_SHIPPING_DAYS} days.`;
  return `${why} creators had to start by ${last}, which has passed.`;
};

/** Firestore Timestamp (admin/client, or its JSON `_seconds` form) or Date -> Date, else null. */
const timestampToDate = (t: unknown): Date | null => {
  if (t instanceof Date) return t;
  const ts = t as { seconds?: number; _seconds?: number; nanoseconds?: number; _nanoseconds?: number; toDate?: () => Date } | null | undefined;
  if (typeof ts?.toDate === 'function') return ts.toDate();
  const seconds = ts?.seconds ?? ts?._seconds;
  // Nanoseconds kept: a 23:59:59.999 deadline must not read as already passed in its last second.
  const nanoseconds = ts?.nanoseconds ?? ts?._nanoseconds ?? 0;
  return typeof seconds === 'number' ? new Date(seconds * 1000 + nanoseconds / 1e6) : null;
};

/** Firestore Timestamp, Date or ISO string -> Date, else null (Flask's _parse_iso_utc). */
const historyTimeToDate = (t: unknown): Date | null => {
  if (typeof t !== 'string') return timestampToDate(t);
  const d = new Date(t);
  return Number.isNaN(d.getTime()) ? null : d;
};

type DraftSlot = { status?: string; draftApproved?: boolean; linkHistory?: Array<{ type?: string; timestamp?: unknown }>; contentHistory?: Array<{ status?: string; timestamp?: unknown }> };

/**
 * The collab's draft-carrying slots (voided excluded) and whether they are UGC units. A collab with
 * non-voided content units drafts through them (its links are seeded from them and only post);
 * otherwise its deliverable links carry the drafts. Mirrors Flask's SchedulingService._draft_slots.
 */
const draftSlots = (collab: Pick<Collab, 'deliverableLinks' | 'content'>): { slots: DraftSlot[]; ugc: boolean } => {
  const units = Object.values(collab.content ?? {}).filter((unit) => unit && unit.status !== 'voided') as DraftSlot[];
  if (units.length) return { slots: units, ugc: true };
  const links = Object.values(collab.deliverableLinks ?? {})
    .flatMap((slot) => (Array.isArray(slot) ? slot : []))
    .filter((link) => link && link.status !== 'voided') as DraftSlot[];
  return { slots: links, ugc: false };
};

/**
 * Every draft approved: at least one non-voided draft slot, and every one approved (a UGC unit
 * 'approved', a link with `draftApproved`). Rejected or unknown statuses count as not done.
 * Mirrors Flask's draft_phase_done.
 */
export const draftPhaseDone = (collab: Pick<Collab, 'deliverableLinks' | 'content'>): boolean => {
  const { slots, ugc } = draftSlots(collab);
  return slots.length > 0 && slots.every((slot) => (ugc ? slot.status === 'approved' : !!slot.draftApproved));
};

/**
 * When the last draft was approved: the latest, over the draft slots, of each slot's FIRST approval
 * in its history (linkHistory type 'approved' / contentHistory status 'approved'). Unparseable
 * timestamps are skipped; null when no slot records one. Mirrors Flask's draft_phase_done_at.
 */
export const draftPhaseDoneAt = (collab: Pick<Collab, 'deliverableLinks' | 'content'>): Date | null => {
  const { slots, ugc } = draftSlots(collab);
  let last: number | null = null;
  for (const slot of slots) {
    const times = ugc
      ? (slot.contentHistory ?? []).filter((e) => e?.status === 'approved').map((e) => historyTimeToDate(e.timestamp))
      : (slot.linkHistory ?? []).filter((e) => e?.type === 'approved').map((e) => historyTimeToDate(e.timestamp));
    const valid = times.filter((t): t is Date => !!t).map((t) => t.getTime());
    if (valid.length) last = Math.max(last ?? -Infinity, Math.min(...valid));
  }
  return last == null ? null : new Date(last);
};

/** Calendar day ('YYYY-MM-DD') of `instant` in `timezone`. */
const ymdIn = (instant: Date, timezone: string): string => new Date(localDayMs(instant, timezone)).toISOString().slice(0, 10);

export type DraftPhaseCollab = Pick<Collab, 'deliverableLinks' | 'content' | 'contentDeadlineKind'>;

/**
 * The day after a late draft approval, or null. A non-post collab whose drafts are all approved,
 * the last one on or after the `goLiveEndYmd` calendar day in `timezone`, may post on the day after that
 * approval. Keyed on the stored approval time, never on now: Flask's _go_live_anchor.
 */
export const lateApprovalPostingDay = (collab: DraftPhaseCollab, goLiveEndYmd: string, timezone: string): string | null => {
  if (collab.contentDeadlineKind === 'post' || !draftPhaseDone(collab)) return null;
  const tz = safeTz(timezone);
  const doneAt = draftPhaseDoneAt(collab);
  if (!doneAt) return null;
  const doneDay = ymdIn(doneAt, tz);
  if (doneDay < goLiveEndYmd) return null;
  return addDaysYmd(doneDay, 1);
};

/** Calendar days a posting date may fall on: the go-live window, or only lateApprovalPostingDay when it applies. */
export const postingDayBounds = (
  collab: DraftPhaseCollab,
  window: { start: string; end: string },
  timezone: string
): { min: string; max: string } => {
  const late = lateApprovalPostingDay(collab, window.end, timezone);
  return late ? { min: late, max: late } : { min: window.start, max: window.end };
};

/**
 * End of the go-live window the collab is held to (goLiveWindowFor; end of its last day in
 * `timezone`), or null when go-live doesn't apply. A collab whose drafts were all approved after
 * that window ends gets the end of the day after the approval instead (lateApprovalPostingDay).
 * Never reads contentDueDate: that is the draft / turnaround clock, not go-live.
 */
export const getPostByDeadline = (
  collab: (GoLiveCollab & Partial<DraftPhaseCollab>) | null | undefined,
  campaign: GoLiveFields | null | undefined,
  timezone: string
): Date | null => {
  const window = goLiveWindowFor(collab, campaign);
  if (!window) return null;
  return endOfDayInTimezone((collab && lateApprovalPostingDay(collab, window.end, timezone)) ?? window.end, timezone);
};

/**
 * A contentDueDate on a collab that has not been accepted yet, honoured only when the brand approved
 * it (contentDueDateExtendedAt, written by the dashboard) and never past `deadline` (the campaign's
 * own turnaround due date) + MAX_DUE_DATE_EXTENSION_DAYS. Null otherwise: the caller stamps the
 * campaign deadline.
 */
export const preAcceptExtendedDueDate = (
  collab: Pick<Collab, 'contentDueDate' | 'contentDueDateExtendedAt'> | null | undefined,
  deadline: Date,
  timezone?: string | null
): Date | null => {
  const due = collab?.contentDueDateExtendedAt ? timestampToDate(collab.contentDueDate) : null;
  if (!due) return null;
  const cap = computeContentDueDate(deadline, 0, MAX_DUE_DATE_EXTENSION_DAYS, safeTz(timezone));
  return due.getTime() > cap.getTime() ? cap : due;
};
